import React from 'react'
import ReactPaginate, { ReactPaginateProps } from 'react-paginate'
import styled, { css } from 'styled-components'
import chevronLeft from 'uiComponents/atoms/Icon/assets/ic-chevron-left-grey.svg'
import {
  backgroundColor,
  black,
  darkGrayColor,
  granite,
  inactiveColor,
  outline,
  outlineHover,
  primaryColor,
} from 'uiComponents/constants/colors'
import {
  fontFamilyPrimary,
  fsS,
  fwBold,
  fwMediumBold,
} from 'uiComponents/constants/typography'

type IPaginationProps = ReactPaginateProps

const Button = css`
  display: inline-block;
  width: 36px;
  height: 36px;
  border: 1px solid ${outline};
  border-radius: 2px;
  cursor: pointer;
  position: relative;
  user-select: none;

  &:focus,
  a:focus {
    outline: none;
  }

  &:hover {
    border-color: ${outlineHover};
  }

  &:active {
    border-color: ${black};
  }

  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    margin: -4px 0 0 -3px;
    width: 6px;
    height: 10px;
    background-image: url('${chevronLeft}');
    background-repeat: no-repeat;
    background-size: 6px 10px;
  }
`

const Wrapper = styled.div`
  margin-top: 45px;
  font-size: ${fsS}px;
  ${fontFamilyPrimary};
  height: 36px;
  position: relative;
  user-select: none;
  text-align: center;

  .ellipsis {
    vertical-align: top;
    display: inline-block;
    height: 36px;
    line-height: 36px;
    padding: 0 10px;
    color: ${granite};
  }

  .pageNumbers {
    vertical-align: top;
    display: inline-block;
    margin: 0 5px;
    ${fwMediumBold};

    a {
      color: ${inactiveColor};
      vertical-align: top;
      display: inline-block;
      border-radius: 2px;
      width: 36px;
      height: 36px;
      text-align: center;
      line-height: 35px;
      cursor: pointer;
      outline: none;
      font-size: ${fsS}px;
      color: ${granite};

      &:hover {
        color: ${darkGrayColor};
        background-color: ${backgroundColor};
      }
    }
  }

  .active {
    ${fwBold};
    color: ${black};
    border: 1px solid ${primaryColor};
    border-radius: 2px;

    a {
      color: ${primaryColor};
      height: 34px;

      &:hover {
        color: ${primaryColor};
      }
    }
  }

  .buttonLink {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    cursor: pointer;
  }

  .buttonPrev {
    ${Button};
    margin-right: 12px;
  }

  .buttonNext {
    ${Button};
    margin-left: 12px;

    &::before {
      margin-left: -2px;
      transform: rotate(180deg);
    }
  }
`

const Pagination: React.FunctionComponent<IPaginationProps> = ({
  marginPagesDisplayed,
  pageCount,
  pageRangeDisplayed,
  ...props
}) => {
  const styleProps = {
    activeClassName: 'active',
    breakClassName: 'ellipsis',
    nextClassName: 'buttonNext',
    previousClassName: 'buttonPrev',
    previousLabel: '',
    nextLabel: '',
    pageClassName: 'pageNumbers',
    nextLinkClassName: 'buttonLink',
    previousLinkClassName: 'buttonLink',
  }

  return (
    <Wrapper>
      <ReactPaginate
        marginPagesDisplayed={marginPagesDisplayed}
        nextLabel={'\u3031'}
        pageCount={pageCount}
        pageRangeDisplayed={pageRangeDisplayed}
        previousLabel={'\u3031'}
        {...styleProps}
        {...props}
      />
    </Wrapper>
  )
}

export default Pagination
